'use client'

import { useLocale, useTranslations } from 'next-intl'
import { type KeyboardEvent, useMemo, useState } from 'react'

import type { ModelsFeaturedClub } from '@/api/generated-api'
import PartnerAp from '@/assets/icons/partner-ap.svg'
import PartnerCalendar from '@/assets/icons/partner-calendar.svg'
import PartnerUsers from '@/assets/icons/partner-users.svg'
import TinyDot from '@/assets/icons/tiny-dot.svg'
import { URLS } from '@/configs/urls'
import { useFeaturedClubs } from '@/features/clubs/hooks'
import { clubSportName } from '@/features/clubs/lib/resolveClubSportDisplay'
import type { PublicSportType } from '@/features/sport-types/api/sportTypesApi'
import { useSportTypes } from '@/features/sport-types/hooks'
import { useUserMe } from '@/hooks/useUserMe'
import { Link } from '@/i18n/routing'
import { getDatePartsInZone } from '@/lib/datetime/formatDateTime'
import { useIsHydrated } from '@/lib/datetime/useIsHydrated'
import { localizeCountryName } from '@/lib/localizeCountryName'
import { useAuth } from '@/providers/AuthProvider'
import Button from '@/shared/ui/Button'
import { useModalStore } from '@/stores/modalStore'
import Image from '@/ui/MediaImage'
import {
  sanitizeExternalImageUrl,
  shouldUnoptimizeImageSrc
} from '@/utils/imageUrl'

import {
  DEFAULT_CLUB_LOGO,
  MEMBER_TABS
} from '../forClubsLandingContent.constants'
import { pickMemberClubs } from '../forClubsLandingContent.lib'
import type { ClubCard, MemberTabValue } from '../forClubsLandingContent.model'
import S from './ForClubsMembers.module.scss'

const FEATURED_CARDS_LIMIT = 8

const TAB_TO_SPORT_TYPE: Partial<Record<MemberTabValue, string>> = {
  football: 'FOOTBALL',
  basketball: 'BASKETBALL',
  americanFootball: 'AMERICAN_FOOTBALL'
}

const MONTH_KEYS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
]

function featuredToClubCard(
  club: ModelsFeaturedClub,
  sportTypes: PublicSportType[]
): ClubCard {
  return {
    id: club.id,
    name: club.name?.trim() || '—',
    country: (club.country || '').trim() || '—',
    sport: clubSportName(club, sportTypes),
    fans: String(club.fan_count ?? 0),
    points: `${club.ap_score ?? 0} AP`,
    // Instant join timestamp, formatted to the viewer's local month/year at
    // render time. Static mock cards carry a pre-formatted `joined` string.
    joined: '',
    joinedAt: club.joined_at ?? undefined,
    logoUrl: sanitizeExternalImageUrl(club.logo_url)
  }
}

function parseJoinedValue(value: string) {
  const match = /^Joined ([A-Za-z]{3}) (\d{4})$/.exec(value.trim())
  if (!match) return null

  return {
    month: match[1],
    year: match[2]
  }
}

function MemberClubLogo({
  initialSrc,
  alt
}: {
  initialSrc: string
  alt: string
}) {
  const [src, setSrc] = useState(initialSrc)
  const [canFallback, setCanFallback] = useState(true)

  return (
    <Image
      src={src}
      alt={alt}
      width={60}
      height={60}
      className={S.memberLogoImage}
      unoptimized={shouldUnoptimizeImageSrc(src)}
      onError={() => {
        if (canFallback && src !== DEFAULT_CLUB_LOGO) {
          setSrc(DEFAULT_CLUB_LOGO)
          setCanFallback(false)
        }
      }}
    />
  )
}

const ForClubsMembers = () => {
  const t = useTranslations('forClubsPage.members')
  const locale = useLocale()
  const hydrated = useIsHydrated()
  const { openAuthModal } = useAuth()
  const { data: user } = useUserMe()
  const { data: featuredClubs } = useFeaturedClubs()
  const { data: sportTypes = [] } = useSportTypes()
  const isGuest = !user
  const [activeTab, setActiveTab] = useState<MemberTabValue>('all')

  const cards = useMemo<ClubCard[]>(() => {
    if (!featuredClubs?.length) {
      return []
    }

    return pickMemberClubs(
      featuredClubs,
      TAB_TO_SPORT_TYPE[activeTab],
      FEATURED_CARDS_LIMIT
    ).map(club => featuredToClubCard(club, sportTypes))
  }, [featuredClubs, activeTab, sportTypes])

  function handleEmptyCardClick() {
    if (!isGuest) return
    useModalStore.getState().setSignupDefaultEntityType('CLUB')
    openAuthModal('emailSignupEntityChoose', 'landing')
  }

  const renderJoinedLabel = (card: ClubCard): string => {
    if (card.joinedAt) {
      const parts = getDatePartsInZone(
        card.joinedAt,
        hydrated ? undefined : 'UTC'
      )
      if (!parts) {
        return '—'
      }
      const monthKey = MONTH_KEYS[parts.monthIndex]
      return t('joinedLabel', {
        month: t(`months.${monthKey}`, { default: monthKey }),
        year: String(parts.year)
      })
    }
    const joinedValue = parseJoinedValue(card.joined)
    if (!joinedValue) {
      return card.joined
    }
    return t('joinedLabel', {
      month: t(`months.${joinedValue.month}`, { default: joinedValue.month }),
      year: joinedValue.year
    })
  }

  return (
    <section className={S.membersSection}>
      <div className={S.sectionHeader}>
        <p className={S.sectionEyebrow}>{t('eyebrow')}</p>
        <h2 className={S.sectionTitle}>{t('headline')}</h2>
      </div>
      <div className={S.tabsSection}>
        <div className={S.tabs}>
          {MEMBER_TABS.map(({ value, labelKey, isDisabled, hasSoonBadge }) => {
            const isActive = activeTab === value
            return (
              <div
                key={value}
                className={`${S.tabItem} ${
                  value === 'all' ? S.tabItemWide : ''
                } ${hasSoonBadge ? S.tabItemWithBadge : ''}`}
              >
                <Button
                  type="button"
                  color="secondary"
                  size="m"
                  fullWidth={value === 'all'}
                  className={`${S.tab} ${isActive ? S.tabActive : ''} ${
                    isDisabled ? S.tabDisabled : ''
                  }`}
                  disabled={isDisabled}
                  aria-disabled={isDisabled}
                  aria-pressed={isActive}
                  onClick={isDisabled ? undefined : () => setActiveTab(value)}
                >
                  {t(labelKey)}
                </Button>
                {hasSoonBadge && (
                  <span className={S.soonBadge}>{t('soonBadge')}</span>
                )}
              </div>
            )
          })}
        </div>
      </div>
      <div className={S.membersGrid}>
        {cards.map(card => {
          const cardBody = (
            <>
              <div className={S.memberHead}>
                <div className={S.memberLogo}>
                  <MemberClubLogo
                    key={`${card.name}-${
                      card.image || card.logoUrl || DEFAULT_CLUB_LOGO
                    }`}
                    initialSrc={card.image || card.logoUrl || DEFAULT_CLUB_LOGO}
                    alt={card.name}
                  />
                </div>
                <div className={S.memberInfo}>
                  <h3 className={S.memberName}>{card.name}</h3>
                  <p className={S.memberLocation}>
                    <span>{localizeCountryName(card.country, locale)}</span>
                    <TinyDot className={S.memberLocationDot} aria-hidden />
                    <span>
                      {t.has(`sports.${card.sport}`)
                        ? t(`sports.${card.sport}`)
                        : card.sport}
                    </span>
                  </p>
                </div>
              </div>
              <div className={S.memberMeta}>
                <div className={S.memberMetaItem}>
                  <PartnerUsers className={S.memberMetaIcon} aria-hidden />
                  <span>{card.fans}</span>
                </div>
                <div className={S.memberMetaItem}>
                  <PartnerAp className={S.memberMetaIcon} aria-hidden />
                  <span>{card.points}</span>
                </div>
                <div className={S.memberMetaItem}>
                  <PartnerCalendar className={S.memberMetaIcon} aria-hidden />
                  <span>{renderJoinedLabel(card)}</span>
                </div>
              </div>
            </>
          )
          // Real club cards link to /clubs/{id}; mock showcase cards (no id)
          // stay non-interactive.
          return card.id ? (
            <Link
              key={card.id}
              href={URLS.CLUB_DETAIL(card.id)}
              className={S.memberCard}
            >
              {cardBody}
            </Link>
          ) : (
            <article key={card.name} className={S.memberCard}>
              {cardBody}
            </article>
          )
        })}
        {isGuest && (
          <article
            className={S.memberCardEmpty}
            role="button"
            tabIndex={0}
            onClick={handleEmptyCardClick}
            onKeyDown={(event: KeyboardEvent<HTMLElement>) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                handleEmptyCardClick()
              }
            }}
          >
            <div className={S.memberCardEmptyIcon} aria-hidden>
              <span className={S.memberCardEmptyPlus} />
            </div>
            <div className={S.memberCardEmptyContent}>
              <p>{t('yourClubHere')}</p>
              <span>{t('registerForFree')}</span>
            </div>
          </article>
        )}
      </div>
    </section>
  )
}

export default ForClubsMembers
